/**
 * Navigation. Every screen states the business question it answers —
 * a screen that cannot name its question does not belong in the product.
 */
export interface NavItem {
  readonly path: string;
  readonly label: string;
  readonly question: string;
  readonly group: string;
}

export const NAV_ITEMS: readonly NavItem[] = [
  { path: '/', label: 'Command Centre', question: 'What has our advertising spent, and what did it produce?', group: 'Overview' },
  { path: '/campaigns', label: 'Campaigns', question: 'Which campaigns are generating qualified pipeline?', group: 'Advertising' },
  { path: '/keywords', label: 'Keywords', question: 'Which keywords produce opportunities, and at what cost?', group: 'Advertising' },
  { path: '/search-terms', label: 'Search Terms', question: 'What are people actually searching, and what is wasting money?', group: 'Advertising' },
  { path: '/geography', label: 'Geography', question: 'Which towns are worth more advertising money?', group: 'Advertising' },
  { path: '/linkedin', label: 'LinkedIn', question: 'Is audience-led advertising reaching the right decision-makers?', group: 'Advertising' },
  { path: '/landing-pages', label: 'Landing Pages', question: 'Which pages convert, and which produce poor-quality leads?', group: 'Conversion' },
  { path: '/conversions', label: 'Conversions', question: 'Where in the funnel are we losing people?', group: 'Conversion' },
  { path: '/pipeline', label: 'Pipeline', question: 'How much qualified pipeline and revenue has advertising generated?', group: 'Commercial' },
  { path: '/budget', label: 'Budget', question: 'Are we pacing correctly, and what will we spend this month?', group: 'Commercial' },
  { path: '/recommendations', label: 'Recommendations', question: 'What should we change today, and what is the evidence?', group: 'Intelligence' },
  { path: '/analyst', label: 'AI Analyst', question: 'Ask a question of the advertising and CRM data.', group: 'Intelligence' },
  { path: '/integrations', label: 'Integrations', question: 'Is our data actually arriving, and is it trustworthy?', group: 'Operations' },
  { path: '/audit', label: 'Audit', question: 'Who changed what, when, and why?', group: 'Operations' },
  { path: '/settings', label: 'Settings', question: 'How is the platform configured?', group: 'Operations' },
];

export const NAV_GROUPS = ['Overview', 'Advertising', 'Conversion', 'Commercial', 'Intelligence', 'Operations'] as const;

/*
  Which screens are actually built, as opposed to a placeholder.

  This lived in App.tsx while the status badge in the shell was a HARDCODED
  string reading "Milestone 3 · foundation" — written at the start and never
  touched again. It was still saying that with the campaigns live, the tracking
  installed and eleven screens working. A status that is typed by hand is a
  status that will be wrong, so the badge is now derived from this list and
  cannot drift from it.
*/
export const BUILT_PATHS: ReadonlySet<string> = new Set([
  '/', '/integrations', '/campaigns', '/keywords', '/budget', '/audit',
  '/landing-pages', '/settings', '/analyst', '/search-terms', '/geography', '/conversions', '/recommendations',
]);

export const buildProgress = (): { built: number; total: number } =>
  ({ built: NAV_ITEMS.filter((i) => BUILT_PATHS.has(i.path)).length, total: NAV_ITEMS.length });
